import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { LocalizationService } from '../../services/LocalizationService';
import { IAButton } from '../button/Button';
import { IATreeLink } from '../treeLink';
import { ContentMapStore } from './store/ContentMapStore';
import * as styles from './ContentMap.css';
import { IACommandBar } from '../commandBar/CommandBar';
import { IACardViewService } from '../messagesCardView/CardView.service';
import { ITreeLinkNode } from '../../interfaces/ITreeLinkNode';
import ContentOrganizerService from '../../services/ContentOrganizerService';
import { IContentMap } from '../../interfaces/IContentMap';
import { Helper } from '../../Helper';
import { TargetingService } from '../../services/TargetingService';
import { IApplication, IDefaultValues } from '../../interfaces/IApplication';
import { DesignEditPanel } from './panel/DesignEditPanel';
import { IAPopup } from '../popup/Popup';
import { runInAction } from 'mobx';
import { IHttpStatus } from '../../interfaces/IHttpStatus';
import { ITargetingApplications } from '../../interfaces/ITargetingApplications';
import { LoggingService } from '../../services/LoggingService';

export interface IContentMapProps {
  contentMapStore?: ContentMapStore;
  hasChanged: string;
  onClick: () => void;
  showPage?: (page: string) => void;
}


@inject('contentMapStore')
@observer
export class ContentMap extends React.Component<IContentMapProps> {

  private cardViewService: IACardViewService = new IACardViewService();
  private localizationService: LocalizationService = new LocalizationService();
  private contentMapId: string;

  constructor(props: IContentMapProps) {
    super(props);
    LoggingService.init(this.props.contentMapStore?.environment);
    this.contentMapId = Helper.getRandomStringKey();
    window.addEventListener('resize', () => this.resetRowHeights());
    this.props.contentMapStore?.setIsMobile(window.innerWidth < 667);
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
    if (localStorage.getItem('targetedPreviewIds')) {
      this.props.contentMapStore.targetedPreviewIds = JSON.parse(localStorage.getItem('targetedPreviewIds'));
    }
  }

  public componentDidMount(): void {
    this.showSkeleton();
    if (!this.props.contentMapStore?.inEditMode && this.props.contentMapStore?.instance) {
      let contentMapFromLocalStorage = localStorage.getItem(`IntraActive-ContentMap-${this.props.contentMapStore?.instance?.instanceName}`);
      if (contentMapFromLocalStorage == null || contentMapFromLocalStorage === "null") {
        localStorage.removeItem(`IntraActive-ContentMap-${this.props.contentMapStore?.instance?.instanceName}`);
        contentMapFromLocalStorage = undefined;
      }
      if (contentMapFromLocalStorage && JSON.parse(contentMapFromLocalStorage)?.configuration !== undefined) {
        this.props.contentMapStore?.setContentMap(JSON.parse(contentMapFromLocalStorage));
        this.props.contentMapStore?.setOriginalContentMap(localStorage.getItem(`IntraActive-ContentMap-${this.props.contentMapStore?.instance?.instanceName}`));
        this.resetRowHeights();
      }
    }
    TargetingService.getApplication(this.props.contentMapStore?.environment, this.props.contentMapStore?.tenant, this.props.contentMapStore?.token, "Content Organizer", false).then((result: { httpStatus: IHttpStatus, value?: ITargetingApplications[] }) => {
      this.props.contentMapStore?.setApplicationSettings(result.value[0]);
      let application: IApplication;
      if (result.value && result.value.length > 0) {
        const appData = JSON.parse(result.value[0].defaultValues);
        let defaultValues: IDefaultValues;
        if (!appData || Array.isArray(appData)) {
          const list: any[] = [];
          defaultValues = {
            isEnable: false,
            type: "profileData",
            values: list,
            allValues: false
          };
          application = {
            id: undefined,
            title: result.value[0].title,
            name: result.value[0].title,
            instances: [],
            default: defaultValues
          };
        } else {
          application = appData;
          application.default.type = result?.value?.length > 0 && result?.value[0].applicationSettings?.toLocaleLowerCase() === "profile data" ? "profileData" : "userGroups";
        }
        runInAction(() => {
          if (this.props.contentMapStore?.applicationSettings) {
            this.props.contentMapStore.applicationSettings.application = application;
          }
        });
        this.loadContentMap();
      }
    });
    if (localStorage.getItem(`IntraActive-ContentMap-${this.props.contentMapStore?.instance?.instanceName}-Edited`)) {
      this.props.contentMapStore?.setShowForgotToSavePopup(true);
    }
  }

  public componentWillReceiveProps(): void {
    if (!localStorage.getItem(`IntraActive-ContentMap-${this.props.contentMapStore?.instance?.instanceName}-Edited`) && !this.props.contentMapStore?.showForgotToSavePopup) {
      this.loadContentMap(true);
    }
  }

  private resetRowHeights(): void {
    this.props.contentMapStore?.setResetRowHeights(true);
    this.getTreeLinkList();
    setTimeout(() => {
      this.getTreeLinkList();
    }, 0);
  }

  private loadContentMap(shouldReload?: boolean): void {
    let targetingIsEnabled;
    if (this.props.contentMapStore?.applicationSettings?.application?.instances.length > 0) {
      this.props.contentMapStore?.applicationSettings?.application?.instances.forEach((instance) => {
        if (instance?.id === this.props.contentMapStore?.instance?.instanceName) {
          targetingIsEnabled = instance.isEnable;
        }
      });
    }
    if (localStorage.getItem('targetedPreviewIds')) {
      targetingIsEnabled = false;
    }
    ContentOrganizerService.getContentMap(this.props.contentMapStore?.environment, this.props.contentMapStore?.token, this.props.contentMapStore?.tenant, this.props.contentMapStore?.instance?.instanceName, this.props.contentMapStore?.inEditMode ? false : (targetingIsEnabled !== undefined ? targetingIsEnabled : false)).then((contentMap: IContentMap) => {
      if (contentMap && !contentMap.configuration) {
        contentMap.configuration = {
          treeLinkNodes: [],
          design: {
            highlightColor: this.props.contentMapStore?.highlightColor,
            backgroundColor: undefined,
            lineColor: "#eeeeee",
            textColor: this.props.contentMapStore?.hasDarkBackground ? "#ffffff" : "#333333",
            iconColor: this.props.contentMapStore?.hasDarkBackground ? "#ffffff" : "#333333",
            defaultIcon: "Link",
            title: "Content map",
            showTitle: true,
            showLine: true,
            dropShadow: false,
            showIconOnItems: true,
            amountOfFirstLevelItemsToShow: 3,
            amountOfColumns: 5,
            expandAsDefault: false
          }
        };
      }
      if (contentMap && contentMap.configuration && !contentMap.configuration.design) {
        contentMap.configuration.design = {
          highlightColor: this.props.contentMapStore?.highlightColor,
          backgroundColor: undefined,
          lineColor: "#eeeeee",
          textColor: this.props.contentMapStore?.hasDarkBackground ? "#ffffff" : "#333333",
          iconColor: this.props.contentMapStore?.hasDarkBackground ? "#ffffff" : "#333333",
          defaultIcon: "Link",
          title: "Content map",
          showTitle: true,
          showLine: true,
          dropShadow: false,
          showIconOnItems: true,
          amountOfFirstLevelItemsToShow: 3,
          amountOfColumns: 5,
          expandAsDefault: false
        };
      }
      if (
        localStorage.getItem(`IntraActive-ContentMap-${this.props.contentMapStore?.instance?.instanceName}`) &&
        JSON.parse(localStorage.getItem(`IntraActive-ContentMap-${this.props.contentMapStore?.instance?.instanceName}`)).configuration !== undefined &&
        localStorage.getItem(`IntraActive-ContentMap-${this.props.contentMapStore?.instance?.instanceName}`) === JSON.stringify(contentMap) &&
        !this.props.contentMapStore?.inEditMode &&
        !this.props.contentMapStore?.showForgotToSavePopup
      ) {
        if (JSON.stringify(contentMap) !== JSON.stringify(this.props.contentMapStore?.contentMap) || shouldReload) {
          this.props.contentMapStore?.setContentMap(contentMap);
          this.resetRowHeights();
        }
      } else {
        this.props.contentMapStore?.setContentMap(contentMap);
        this.props.contentMapStore?.setOriginalContentMap(JSON.stringify(contentMap));
        if (!this.props.contentMapStore?.inEditMode) {
          Helper.setLocalStorage(`IntraActive-ContentMap-${this.props.contentMapStore?.instance?.instanceName}`, this.props.contentMapStore?.originalContentMap);
        }
        this.resetRowHeights();
      }
      if (contentMap && (!contentMap.configuration || contentMap.configuration?.treeLinkNodes == undefined || contentMap.configuration?.treeLinkNodes?.length === 0)) {
        this.props.contentMapStore.content = undefined;
      }
    });
  }

  private getTreeLinkList(): void {
    if (this.props.contentMapStore?.contentMap && this.props.contentMapStore?.contentMap.configuration && this.props.contentMapStore?.contentMap?.configuration?.design && this.props.contentMapStore?.contentMap.configuration.treeLinkNodes?.length > 0) {
      this.props.contentMapStore?.contentMap.configuration.treeLinkNodes.forEach((treeLinkNode: ITreeLinkNode) => {
        if (treeLinkNode.config.title === undefined) {
          this.props.contentMapStore?.setDisableAddButton(true);
        } else {
          this.props.contentMapStore?.setDisableAddButton(false);
        }
      });
      const list: JSX.Element[] = [];
      const rowHeights = [];
      // Resizing cards
      const contentMapContainer: any = document.getElementById("IA_ContentMapContainer");
      if (contentMapContainer && this.props.contentMapStore?.contentMap) {
        const [numberOfCardsPerRow, maxCardWidthAvailable] = this.cardViewService.getNumberOfCardsPerRowAndMaxCardWidthAvailable(Math.floor(contentMapContainer.clientWidth), 20, 212, this.props.contentMapStore?.contentMap?.configuration?.design.amountOfColumns);
        let currentRowNumber = 1;
        let rowMaxHeight = 0;
        this.props.contentMapStore?.contentMap.configuration.treeLinkNodes.map((treeLink: ITreeLinkNode, index) => {
          const rowIndex = Math.floor(index / numberOfCardsPerRow);
          if (document.getElementsByClassName(`row_${this.contentMapId}_${rowIndex}`) && !this.props.contentMapStore?.resetRowHeights) {
            for (let i = 0; i < numberOfCardsPerRow; i++) {
              if (document.getElementsByClassName(`row_${this.contentMapId}_${rowIndex}`)[i]) {
                if (document.getElementsByClassName(`row_${this.contentMapId}_${rowIndex}`)[i].clientHeight > rowMaxHeight) {
                  rowMaxHeight = document.getElementsByClassName(`row_${this.contentMapId}_${rowIndex}`)[i].clientHeight;
                }
              }
            }
            rowHeights[rowIndex] = rowMaxHeight;
          }
          if (index === 0 && this.props.contentMapStore?.dropAreaTreeLinkGroup && this.props.contentMapStore?.dropAreaTreeLinkGroup.config.id === treeLink.config.id) {
            list.push(
              <div
                className={`row_${this.contentMapId}_${rowIndex}`}
                style={{
                  display: "inline-flex",
                  verticalAlign: "top",
                  width: maxCardWidthAvailable,
                  height: maxCardWidthAvailable,
                  marginRight: currentRowNumber == numberOfCardsPerRow ? 0 : 20,
                  // width: this.props.contentMapStore?.isMobile ? "100%" : "auto"
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
                onDragEnter={() => {
                  this.props.contentMapStore?.setDropAsFirst(true);
                }}
              >
                <div
                  className={styles.IA_groupDropzone}
                  style={{
                    borderColor: this.props.contentMapStore?.highlightColor,
                    width: maxCardWidthAvailable,
                  }}
                />
              </div>
            );
            if (currentRowNumber == numberOfCardsPerRow) {
              currentRowNumber = 1;
              rowMaxHeight = 0;
            } else {
              currentRowNumber++;
            }
          }
          let textColor = this.props.contentMapStore?.contentMap?.configuration?.design?.textColor;
          let lineColor = this.props.contentMapStore?.contentMap?.configuration?.design?.lineColor;
          let highlightColor = this.props.contentMapStore?.contentMap?.configuration?.design.highlightColor;
          if (this.props.contentMapStore?.contentMap?.configuration?.design.backgroundColor == undefined || this.props.contentMapStore?.contentMap?.configuration?.design.backgroundColor === "transparent") {
            if (this.props.contentMapStore.hasDarkBackground) {
              if (!Helper.isLightColor(textColor)) {
                textColor = "#ffffff";
              }
              if (lineColor === "#333333") {
                lineColor = "#ffffff";
              }
              if (highlightColor === "#333333") {
                lineColor = "#ffffff";
              }
            } else {
              if (Helper.isLightColor(textColor)) {
                textColor = "#333333";
              }
              if (lineColor === "#ffffff") {
                lineColor = "#333333";
              }
              if (highlightColor === "#ffffff") {
                lineColor = "#333333";
              }
            }
          }
          if (
            this.props.contentMapStore?.isExtension &&
            (this.props.contentMapStore?.contentMap?.configuration?.design.backgroundColor == undefined || this.props.contentMapStore?.contentMap?.configuration?.design.backgroundColor === "transparent")
          ) {
            if (Helper.getTextColorBasedOnBackgroundColorDarkness(textColor, false, true)) {
              textColor = "#333333";
            }
            if (lineColor === "#ffffff") {
              lineColor = "#333333";
            }
            if (highlightColor === "#ffffff") {
              lineColor = "#333333";
            }
          }
          list.push(
            <div
              key={`treeLink_${treeLink.config.id}`}
              className={`row_${this.contentMapId}_${rowIndex}`}
              style={{
                display: "inline-flex",
                verticalAlign: "top",
                minHeight: numberOfCardsPerRow !== 1 && rowIndex < rowHeights.length ? rowHeights[rowIndex] : "auto",
                opacity: this.props.contentMapStore?.draggedTreeLinkNode && treeLink.config.id === this.props.contentMapStore?.draggedTreeLinkNode.config.id ? 0.5 : 1
                // width: this.props.contentMapStore?.isMobile ? "100%" : "auto"
              }}
              onDragEnter={() => {
                this.props.contentMapStore?.setDropAsFirst(false);
                if (treeLink?.config?.id !== this.props.contentMapStore?.draggedTreeLinkNode?.config?.id && this.props.contentMapStore?.draggedTreeLinkNode?.config?.nodeLevel === 0) {
                  this.props.contentMapStore?.setDropAreaTreeLinkGroup(treeLink);
                }
                if (treeLink?.config?.id === this.props.contentMapStore?.draggedTreeLinkNode?.config?.id && this.props.contentMapStore?.draggedTreeLinkNode?.config?.nodeLevel === 0) {
                  this.props.contentMapStore?.setDropAreaTreeLinkGroup(undefined);
                }
                this.getTreeLinkList();
              }}
              onDragEnd={(e) => {
                e.preventDefault();
                this.props.contentMapStore?.drop();
                this.getTreeLinkList();
              }}
              onDragOver={(e) => {
                e.preventDefault();
              }}
            >
              <IATreeLink
                environment={this.props.contentMapStore?.environment}
                isMobile={this.props.contentMapStore?.isMobile}
                tenant={this.props.contentMapStore?.tenant}
                token={this.props.contentMapStore?.token}
                fontSlots={this.props.contentMapStore?.fontSlots}
                treeLink={treeLink}
                instance={this.props.contentMapStore?.instance}
                editHighlightColor={textColor}
                openAllLinksExternally={this.props.contentMapStore?.openAllLinksExternally}
                highlightColor={highlightColor}
                backgroundColor={this.props.contentMapStore?.contentMap?.configuration?.design.backgroundColor}
                lineColor={lineColor}
                textColor={textColor}
                iconColor={textColor}
                borderRadius={this.props.contentMapStore?.contentMap?.configuration?.design?.borderRadius}
                defaultIcon={this.props.contentMapStore?.contentMap?.configuration?.design.defaultIcon}
                showLine={this.props.contentMapStore?.contentMap?.configuration?.design.showLine}
                dropShadow={this.props.contentMapStore?.contentMap?.configuration?.design.dropShadow}
                showIconOnItems={this.props.contentMapStore?.contentMap?.configuration?.design.showIconOnItems}
                hasDarkBackground={this.props.contentMapStore?.hasDarkBackground}
                amountOfFirstLevelItemsToShow={this.props.contentMapStore?.contentMap?.configuration?.design.amountOfFirstLevelItemsToShow}
                expandAsDefault={this.props.contentMapStore?.contentMap?.configuration?.design.expandAsDefault}
                applicationSettings={this.props.contentMapStore?.applicationSettings}
                inEditMode={this.props.contentMapStore?.inEditMode}
                isDragging={this.props.contentMapStore?.draggedTreeLinkNode !== undefined}
                showDropZoneForId={this.props.contentMapStore?.dropAreaTreeLinkNode !== undefined ? this.props.contentMapStore?.dropAreaTreeLinkNode.config.id : undefined}
                returnPageUrl={this.props.contentMapStore?.returnPageUrl}
                onDragEnter={(id) => {
                  if (this.props.contentMapStore?.draggedTreeLinkNode && this.props.contentMapStore?.draggedTreeLinkNode.config.nodeLevel !== 0) {
                    if (id && this.props.contentMapStore?.draggedTreeLinkNode.config.id === id.config.id) {
                      this.props.contentMapStore?.setDropAreaTreeLinkNode(undefined);
                    } else {
                      this.props.contentMapStore?.setDropAreaTreeLinkNode(id);
                    }
                    this.getTreeLinkList();
                  }
                }}
                onDrag={(node: ITreeLinkNode) => {
                  if (this.props.contentMapStore?.draggedTreeLinkNode === undefined) {
                    this.props.contentMapStore?.setDraggedTreeLinkNode(node);
                  }
                }}
                onDrop={() => {
                  if (this.props.contentMapStore?.draggedTreeLinkNode !== undefined) {
                    this.props.contentMapStore?.drop();
                    this.getTreeLinkList();
                  }
                }}
                removeTree={() => {
                  this.props.contentMapStore?.removeTree(treeLink);
                  this.forceUpdate();
                }}
                rerender={() => {
                  if (this.props.contentMapStore?.inEditMode) {
                    this.saveEditedContentMap();
                  }
                  this.getTreeLinkList();
                  this.resetRowHeights();
                }}
                save={(treelink: ITreeLinkNode) => {
                  console.log(treelink);
                  if (this.props.contentMapStore?.inEditMode) {
                    this.saveEditedContentMap();
                  }
                }}
                onClick={(node: ITreeLinkNode) => {
                  this.props.onClick();
                  if (LoggingService !== undefined) {
                    LoggingService.trackEvent("LinkClicked", {
                      ActionType: "LinkClicked",
                      Component: "IntraActive-ContentMap",
                      Id: node.config.id,
                      Title: node.config.title,
                      Tenant: this.props.contentMapStore?.tenant,
                      Instance: this.props.contentMapStore?.instance?.instanceName,
                      UserId: this.props.contentMapStore?.token,
                      Environment: this.props.contentMapStore?.environment
                    });
                  }
                }}
                showPage={(page: string) => this.props.showPage(page)}
                styles={{
                  marginRight: currentRowNumber == numberOfCardsPerRow ? 0 : 20,
                  marginBottom: 20,
                  width: maxCardWidthAvailable,
                  clear: currentRowNumber === 1 ? "both" : "none"
                  // maxWidth: this.props.contentMapStore?.isMobile ? "100%" : (window.innerWidth < 667 ? "100%" : 286)
                }}
              />
            </div>
          );
          if (currentRowNumber == numberOfCardsPerRow) {
            currentRowNumber = 1;
            rowMaxHeight = 0;
          } else {
            currentRowNumber++;
          }
          if (this.props.contentMapStore?.dropAreaTreeLinkGroup && this.props.contentMapStore?.dropAreaTreeLinkGroup.config.id === treeLink.config.id) {
            list.push(
              <div
                className={`row_${this.contentMapId}_${rowIndex}`}
                style={{
                  display: "inline-flex",
                  verticalAlign: "top",
                  width: maxCardWidthAvailable,
                  height: maxCardWidthAvailable,
                  marginRight: currentRowNumber == numberOfCardsPerRow ? 0 : 20,
                  // width: this.props.contentMapStore?.isMobile ? "100%" : "auto"
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
              >
                <div
                  className={styles.IA_groupDropzone}
                  style={{
                    borderColor: this.props.contentMapStore?.highlightColor,
                    width: maxCardWidthAvailable,
                  }}
                />
              </div>
            );
            if (currentRowNumber == numberOfCardsPerRow) {
              currentRowNumber = 1;
              rowMaxHeight = 0;
            } else {
              currentRowNumber++;
            }
          }
        });
      }
      this.props.contentMapStore?.setContent(list);
      this.props.contentMapStore?.setResetRowHeights(false);
    }
  }

  private showSkeleton(): void {
    const skeleton = [];
    const contentMapContainer: any = document.getElementById("IA_ContentMapContainer");
    if (contentMapContainer) {
      const [numberOfCardsPerRow, maxCardWidthAvailable] = this.cardViewService.getNumberOfCardsPerRowAndMaxCardWidthAvailable(Math.floor(contentMapContainer.clientWidth), 20, 212, 5);
      for (let i = 0; i < (numberOfCardsPerRow * 2); i++) {
        skeleton.push(
          <div
            className={styles.IA_contentMapSkeleton}
            style={{
              width: maxCardWidthAvailable,
              marginRight: (i + 1) === numberOfCardsPerRow || (i + 1) === numberOfCardsPerRow * 2 || (i + 1) === numberOfCardsPerRow * 3 || (i + 1) === numberOfCardsPerRow * 4 ? 0 : 20
            }}
          />
        );
      }
    } else {
      setTimeout(() => {
        this.render();
      }, 1000);
    }
    this.props.contentMapStore?.setContent(skeleton);
  }

  private saveEditedContentMap(): void {
    if (JSON.stringify(this.props.contentMapStore?.contentMap) !== JSON.stringify(this.props.contentMapStore?.originalContentMap)) {
      Helper.setLocalStorage(`IntraActive-ContentMap-${this.props.contentMapStore?.instance?.instanceName}-Edited`, JSON.stringify(this.props.contentMapStore?.contentMap));
    } else {
      this.clearEditedContentMap();
    }
  }

  private clearEditedContentMap(): void {
    localStorage.removeItem(`IntraActive-ContentMap-${this.props.contentMapStore?.instance?.instanceName}-Edited`);
  }

  public render(): JSX.Element {
    return (
      <div
        id={"IA_ContentMapContainer"}
        style={this.props.contentMapStore?.styles}
      >
        {this.props.contentMapStore?.contentMap && this.props.contentMapStore?.contentMap?.configuration?.design.showTitle &&
          <div
            className={styles.IA_contentMapTitle}
            style={{
              fontFamily: this.props.contentMapStore.fontSlots?.heading?.fontFamily
            }}
          >
            {this.props.contentMapStore?.contentMap?.configuration?.design.title}
          </div>
        }
        {this.props.contentMapStore?.inEditMode &&
          <IACommandBar
            highlightColor={this.props.contentMapStore?.hasDarkBackground ? "#ffffff" : this.props.contentMapStore?.highlightColor}
            leftSideContent={
              <div>
                <IAButton
                  label={this.localizationService.strings.ContentMap_AddNewHeader}
                  buttonColor="transparent"
                  textColor={this.props.contentMapStore?.hasDarkBackground ? "#ffffff" : "#333333"}
                  icon="Add"
                  disbaled={this.props.contentMapStore?.disableAddButton || !this.props.contentMapStore?.contentMap}
                  height={22}
                  iconColor={this.props.contentMapStore?.hasDarkBackground ? "#ffffff" : this.props.contentMapStore?.highlightColor}
                  fontSize={14}
                  dataAutomationId="contentmap-add-new"
                  onClick={() => {
                    this.props.contentMapStore?.addNew();
                    this.getTreeLinkList();
                  }}
                  style={{
                    float: "left"
                  }}
                />
                <IAButton
                  label={this.localizationService.strings.ContentMap_EditDesign}
                  buttonColor="transparent"
                  textColor={this.props.contentMapStore?.hasDarkBackground ? "#ffffff" : "#333333"}
                  icon="Design"
                  disbaled={!this.props.contentMapStore?.contentMap}
                  height={22}
                  iconColor={this.props.contentMapStore?.hasDarkBackground ? "#ffffff" : this.props.contentMapStore?.highlightColor}
                  fontSize={14}
                  dataAutomationId="contentmap-add-new"
                  onClick={() => {
                    this.props.contentMapStore?.setShowDesignEditPanel(true);
                  }}
                  style={{
                    float: "left",
                    marginLeft: 20,

                  }}
                />
              </div>
            }
            rightSideContent={
              <div>
                <IAButton
                  label={this.localizationService.strings.ContentMap_Save}
                  buttonColor={this.props.contentMapStore?.hasDarkBackground ? "#ffffff" : Helper.getTextColorBasedOnBackgroundColorDarkness(this.props.contentMapStore?.highlightColor, this.props.contentMapStore?.highlightColor, "#333333")}
                  textColor={this.props.contentMapStore?.hasDarkBackground ? Helper.getTextColorBasedOnBackgroundColorDarkness(this.props.contentMapStore?.highlightColor, this.props.contentMapStore?.highlightColor, "#ffffff") : Helper.getTextColorBasedOnBackgroundColorDarkness(this.props.contentMapStore?.highlightColor, "#ffffff", "#333333")}
                  fontSize={14}
                  dataAutomationId="edit-mode"
                  showSpinner={this.props.contentMapStore?.saving}
                  disbaled={this.props.contentMapStore?.saving || this.props.contentMapStore?.originalContentMap === JSON.stringify(this.props.contentMapStore?.contentMap)}
                  onClick={() => {
                    this.props.contentMapStore?.setSaving(true);
                    ContentOrganizerService.saveContentMap(this.props.contentMapStore?.environment, this.props.contentMapStore?.token, this.props.contentMapStore?.tenant, this.props.contentMapStore?.instance?.instanceName, this.props.contentMapStore?.contentMap).then(() => {
                      this.props.contentMapStore?.setSaving(false);
                      this.props.contentMapStore?.setOriginalContentMap(JSON.stringify(this.props.contentMapStore?.contentMap));
                      this.clearEditedContentMap();
                    });
                  }}
                  style={{
                    float: "left",
                    marginTop: -5
                  }}
                />
                <IAButton
                  label={this.localizationService.strings.ContentMap_Cancel}
                  buttonColor={"#777777"}
                  textColor={"#ffffff"}
                  fontSize={14}
                  dataAutomationId="edit-mode"
                  disbaled={this.props.contentMapStore?.saving}
                  onClick={() => {
                    this.props.contentMapStore?.setContentMap(JSON.parse(this.props.contentMapStore?.originalContentMap));
                    this.clearEditedContentMap();
                    this.getTreeLinkList();
                  }}
                  style={{
                    float: "left",
                    marginLeft: 10,
                    marginTop: -5
                  }}
                />
              </div>
            }
            showBar={true}
            style={{
              marginBottom: 15
            }}
          />
        }
        <div style={{ display: "inline-block", width: "100%" }}>
          {this.props.contentMapStore?.content != undefined ?
            this.props.contentMapStore?.content
            :
            <div className={styles.IA_noContent}>{this.localizationService.strings.ContentMap_NoContent}</div>
          }
        </div>
        {this.props.contentMapStore?.inEditMode !== undefined &&
          <DesignEditPanel
            rerender={() => {
              this.getTreeLinkList();
              this.saveEditedContentMap();
            }}
          />
        }
        {this.props.contentMapStore?.showForgotToSavePopup &&
          <IAPopup
            headline={this.localizationService.strings.ContentMap_ForgotToSave}
            content={
              <div>
                <div
                  style={{
                    float: "left"
                  }}
                >
                  {this.localizationService.strings.ContentMap_ForgotToSaveMessage}
                </div>
                <div
                  style={{
                    clear: "both",
                    float: "left"
                  }}
                >
                  <IAButton
                    label={this.localizationService.strings.ContentMap_ForgotToSaveButtonSave}
                    buttonColor={this.props.contentMapStore?.highlightColor}
                    showSpinner={this.props.contentMapStore?.saving}
                    disbaled={this.props.contentMapStore?.saving}
                    onClick={() => {
                      this.props.contentMapStore?.setContentMap(JSON.parse(localStorage.getItem(`IntraActive-ContentMap-${this.props.contentMapStore?.instance?.instanceName}-Edited`)));
                      this.props.contentMapStore?.setSaving(true);
                      ContentOrganizerService.saveContentMap(this.props.contentMapStore?.environment, this.props.contentMapStore?.token, this.props.contentMapStore?.tenant, this.props.contentMapStore?.instance?.instanceName, this.props.contentMapStore?.contentMap).then(() => {
                        this.props.contentMapStore?.setSaving(false);
                        this.props.contentMapStore?.setOriginalContentMap(undefined);
                        this.props.contentMapStore?.setShowForgotToSavePopup(false);
                        this.clearEditedContentMap();
                        this.loadContentMap();
                      });
                    }}
                    style={{
                      marginTop: 20,
                      float: "left"
                    }}
                  />
                  <IAButton
                    label={this.localizationService.strings.ContentMap_ForgotToSaveButtonCancel}
                    buttonColor={"#999999"}
                    showSpinner={false}
                    disbaled={this.props.contentMapStore?.saving}
                    onClick={() => {
                      this.props.contentMapStore?.setShowForgotToSavePopup(false);
                      this.props.contentMapStore?.setOriginalContentMap(undefined);
                      this.clearEditedContentMap();
                      this.loadContentMap();
                    }}
                    style={{
                      marginTop: 20,
                      marginLeft: 10,
                      float: "left"
                    }}
                  />
                </div>
              </div>
            }
            close={() => {
              if (!this.props.contentMapStore?.saving) {
                this.props.contentMapStore?.setShowForgotToSavePopup(false);
                this.props.contentMapStore?.setOriginalContentMap(undefined);
                this.loadContentMap();
              }
            }}
          />
        }
      </div>
    )
  }
}